"use client";

import React, { useMemo, useCallback } from "react";

// Context
import { useDetailActionContext } from "@/contexts/DetailActionContext";

// Types
import type {
  DetailAction,
  DetailPageCustomComponents,
  BaseEntity,
} from "./types/detailPage";
import type { DetailActionContext } from "@/contexts/DetailActionContext";

// Hooks
import { useActionFiltering } from "@/lib/hooks/search/useActionFiltering";
import { useActionState } from "./hooks/useActionState";
import { useActionGroups } from "./hooks/useActionGroups";
import { useActionHandlers } from "./hooks/useActionHandlers";

// Sub-components
import { ActionButton } from "./components/ActionButton";
import { ActionDropdown } from "./components/ActionDropdown";
import { ActionCommandSelect } from "./components/ActionCommandSelect";
import { EditToggleButton } from "./components/EditToggleButton";
import { SavePendingButton } from "./components/SavePendingButton";

// ============================================================================
// UNIVERSAL DETAIL ACTIONS COMPONENT
// ============================================================================

interface UniversalDetailActionsProps<T extends BaseEntity> {
  /** Available actions */
  actions: DetailAction<T>[];

  /** Maximum number of actions to show before using dropdown */
  maxActions?: number;

  /** Custom components */
  customComponents?: DetailPageCustomComponents<T>;

  /** Additional CSS classes */
  className?: string;
}

export function UniversalDetailActions<T extends BaseEntity>({
  actions,
  maxActions = 3,
  customComponents = {},
  className = "",
}: UniversalDetailActionsProps<T>) {
  // Get action context from provider
  const actionContext = useDetailActionContext<T>();
  const {
    item,
    isTicketInlineEditMode,
    onToggleTicketInlineEdit,
    hasTicketPendingChanges,
    onSaveTicketPending,
    isTaskInlineEditMode,
    onToggleTaskInlineEdit,
    hasTaskPendingChanges,
    onSaveTaskPending,
  } = actionContext;

  const isTicketDetail = actionContext.pageConfig?.entityName === "tickets";
  const isTaskDetail = actionContext.pageConfig?.entityName === "tasks";

  // Use action state hook
  const { loadingActions, isMobile, setLoadingAction } = useActionState();

  // Role-based action filtering
  const { filterActionsByRole } = useActionFiltering();

  // Filter visible actions
  const visibleActions = useMemo(() => {
    // First filter by role
    const roleFilteredActions = filterActionsByRole(actions);

    return roleFilteredActions.filter((action) => {
      if (action.visible && typeof action.visible === "function") {
        // Only call visible function if item exists
        return item ? action.visible(item) : true;
      }
      return true;
    });
  }, [actions, item, filterActionsByRole]);

  // Use action groups hook
  const { primaryActions, secondaryActions } = useActionGroups({
    visibleActions,
    maxActions,
  });

  // Use action handlers hook
  const { handleAction } = useActionHandlers({
    item,
    actionContext,
    onSetLoadingAction: setLoadingAction,
  });

  // Handle action selection for mobile/command select
  const handleActionSelect = useCallback(
    (actionId: string) => {
      if (
        actionId === "edit" &&
        isTicketDetail &&
        onToggleTicketInlineEdit != null
      ) {
        onToggleTicketInlineEdit();
        return;
      }
      if (
        actionId === "edit" &&
        isTaskDetail &&
        onToggleTaskInlineEdit != null
      ) {
        onToggleTaskInlineEdit();
        return;
      }
      const action = visibleActions.find((a) => a.id === actionId);
      if (action) {
        void handleAction(action);
      }
    },
    [
      visibleActions,
      handleAction,
      isTicketDetail,
      onToggleTicketInlineEdit,
      isTaskDetail,
      onToggleTaskInlineEdit,
    ]
  );

  const handleSaveTicketPending = useCallback(
    () => void onSaveTicketPending?.(),
    [onSaveTicketPending]
  );

  const handleSaveTaskPending = useCallback(
    () => void onSaveTaskPending?.(),
    [onSaveTaskPending]
  );

  // Render primary action buttons
  const renderPrimaryButtons = useCallback(() => {
    const buttons = primaryActions.map((action) => {
      const isLoading = loadingActions.has(action.id);
      const isDisabled = !!(
        action.disabled &&
        typeof action.disabled === "function" &&
        action.disabled(item)
      );
      const label =
        typeof action.label === "function" ? action.label(item) : action.label;

      // Special handling for edit toggle on tickets/tasks
      if (action.id === "edit" && isTicketDetail && onToggleTicketInlineEdit) {
        return (
          <EditToggleButton
            key={action.id}
            isUnlocked={!!isTicketInlineEditMode}
            isDisabled={isDisabled}
            isLoading={isLoading}
            label={label}
            onClick={onToggleTicketInlineEdit}
          />
        );
      }

      if (action.id === "edit" && isTaskDetail && onToggleTaskInlineEdit) {
        return (
          <EditToggleButton
            key={action.id}
            isUnlocked={!!isTaskInlineEditMode}
            isDisabled={isDisabled}
            isLoading={isLoading}
            label={label}
            onClick={onToggleTaskInlineEdit}
          />
        );
      }

      return (
        <ActionButton
          key={action.id}
          action={action}
          isLoading={isLoading}
          isDisabled={isDisabled}
          label={label}
          // validate-memo-stable-props-allow: per-row handler in list render
          onClick={() => handleAction(action)}
        />
      );
    });

    // Add save button if there are pending changes
    if (
      isTicketDetail &&
      isTicketInlineEditMode &&
      hasTicketPendingChanges &&
      onSaveTicketPending
    ) {
      buttons.push(
        <SavePendingButton
          key="save-ticket-pending"
          onClick={handleSaveTicketPending}
        />
      );
    }

    if (
      isTaskDetail &&
      isTaskInlineEditMode &&
      hasTaskPendingChanges &&
      onSaveTaskPending
    ) {
      buttons.push(
        <SavePendingButton
          key="save-task-pending"
          onClick={handleSaveTaskPending}
        />
      );
    }

    return buttons;
  }, [
    primaryActions,
    loadingActions,
    item,
    isTicketDetail,
    isTaskDetail,
    isTicketInlineEditMode,
    isTaskInlineEditMode,
    hasTicketPendingChanges,
    hasTaskPendingChanges,
    onToggleTicketInlineEdit,
    onToggleTaskInlineEdit,
    onSaveTicketPending,
    onSaveTaskPending,
    handleSaveTicketPending,
    handleSaveTaskPending,
    handleAction,
  ]);

  // Always return the same JSX structure to ensure consistent hook calls
  // Use conditional rendering inside the return instead of conditional returns
  return (
    <>
      {/* Mobile: Show all actions in a single ResponsiveCommandSelect */}
      {isMobile ? (
        <div className={`w-full ${className}`}>
          <ActionCommandSelect
            actions={visibleActions}
            item={item}
            onActionSelect={handleActionSelect}
            placeholder="Select Action"
            expandInline={isTicketDetail}
            expandInlineVariant="floating"
            className="w-full"
          />
        </div>
      ) : (
        /* Desktop: primary buttons + secondary as dropdown or expand-inline on ticket detail */
        <div className={`flex items-center gap-2 ${className}`}>
          {renderPrimaryButtons()}

          {secondaryActions.length > 0 &&
            (isTicketDetail ? (
              <ActionCommandSelect
                actions={secondaryActions}
                item={item}
                onActionSelect={handleActionSelect}
                placeholder="More actions..."
                expandInline
                expandInlineVariant="floating"
                className="min-w-[140px]"
              />
            ) : (
              <ActionDropdown
                actions={secondaryActions}
                loadingActions={loadingActions}
                item={item}
                onActionClick={handleAction}
              />
            ))}
        </div>
      )}
    </>
  );
}

export { UniversalDetailActions as UniversalDetailActionsComponent };
